import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLeaflet } from 'react-leaflet';
import L from 'leaflet';
import './Basemap.css';
import { MIN_MAP_ZOOM, MAX_MAP_ZOOM } from './zoomBounds';

export const RETRY_COOLDOWN = 10000;

// Use public tile lifecycle events, not Leaflet's private tile cache.
export function observeTiles(map, layer, notify) {
  const tiles = new Map();
  const update = () => {
    const zoom = Math.round(map.getZoom());
    const bounds = map.getPixelBounds(map.getCenter(), zoom);
    const size = layer.getTileSize();
    const failed = [...tiles.values()].some(({ coords, error }) =>
      error && coords.z === zoom &&
      (coords.x + 1) * size.x > bounds.min.x &&
      coords.x * size.x < bounds.max.x &&
      (coords.y + 1) * size.y > bounds.min.y &&
      coords.y * size.y < bounds.max.y
    );
    notify(failed);
  };
  const start = ({ tile, coords }) => {
    tiles.set(tile, { coords, error: false });
    update();
  };
  const settle = (error) => ({ tile }) => {
    // Ignore late callbacks for unloaded tiles.
    if (tiles.has(tile)) {
      tiles.get(tile).error = error;
      update();
    }
  };
  const unload = ({ tile }) => { tiles.delete(tile); update(); };
  const events = {
    tileloadstart: start, tileerror: settle(true),
    tileload: settle(false), tileunload: unload,
  };
  layer.on(events);
  map.on('moveend zoomend resize', update);
  return () => {
    layer.off(events);
    map.off('moveend zoomend resize', update);
    tiles.clear();
  };
}

export default function Basemap() {
  const { map } = useLeaflet();
  const layerRef = useRef(null);
  const timer = useRef(null);
  const [container, setContainer] = useState(null);
  const [failed, setFailed] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!map) return undefined;
    const element = L.DomUtil.create('div', 'basemap-status');
    L.DomEvent.disableClickPropagation(element);
    L.DomEvent.disableScrollPropagation(element);
    const control = L.control({ position: 'bottomleft' });
    control.onAdd = () => element;
    control.addTo(map);
    setContainer(element);
    const layer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      minZoom: MIN_MAP_ZOOM,
      maxZoom: MAX_MAP_ZOOM,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    });
    layerRef.current = layer;
    const stop = observeTiles(map, layer, setFailed);
    const loaded = () => setRetrying(false);
    layer.on('load', loaded);
    layer.addTo(map);
    return () => {
      clearTimeout(timer.current);
      timer.current = null;
      stop();
      layer.off('load', loaded);
      layer.remove();
      layerRef.current = null;
      control.remove();
    };
  }, [map]);

  const retry = () => {
    if (timer.current || !layerRef.current) return;
    setCooldown(true);
    setRetrying(true);
    timer.current = setTimeout(() => {
      timer.current = null;
      setCooldown(false);
      setRetrying(false);
    }, RETRY_COOLDOWN);
    // One ordinary visible-grid redraw per user action. No cache busting,
    // prefetch, provider change, or automatic retry loop.
    layerRef.current.redraw();
  };

  return container && createPortal(
    <div hidden={!failed && !retrying} className="basemap-status-panel">
      <div role="status" aria-live="polite" aria-atomic="true">
        {failed
          ? 'Map background is incomplete. Markers and coverage controls remain available.'
          : retrying ? 'Retrying map background…' : ''}
      </div>
      <button type="button" onClick={retry} disabled={cooldown}>
        {cooldown ? 'Retry available shortly' : 'Retry map background'}
      </button>
    </div>,
    container
  );
}
